import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiDelete, apiGet, apiPatch } from "@/lib/apiClient";
import { ArrowLeft, Package } from "lucide-react";

type FinanceItem = {
  id: string;
  name?: string;
  description?: string | null;
  sku?: string | null;
  unitPrice?: number;
  currency?: string;
  active?: boolean;
};

function formatMoney(n: number, currency = "USD") {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);
}

export function FinanceItemDetailPage() {
  const navigate = useNavigate();
  const { itemId: idParam } = useParams<{ itemId: string }>();
  const itemId = idParam ? decodeURIComponent(idParam) : "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [item, setItem] = useState<FinanceItem | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sku, setSku] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<FinanceItem>(`/api/finance/items/${encodeURIComponent(itemId)}`);
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load item.");
        setItem(null);
      } else {
        setError(null);
        const it = res.data;
        setItem(it);
        if (it) {
          setName(it.name || "");
          setDescription(it.description || "");
          setSku(it.sku || "");
          setUnitPrice(typeof it.unitPrice === "number" ? String(it.unitPrice) : "0");
          setActive(it.active !== false);
        }
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const onSave = async () => {
    if (!item?.id) return;
    setSaving(true);
    setError(null);
    const res = await apiPatch<FinanceItem>(`/api/finance/items/${encodeURIComponent(item.id)}`, {
      name: name.trim(),
      description: description.trim() || null,
      sku: sku.trim() || null,
      unitPrice: Number(unitPrice) || 0,
      active,
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Save failed.");
      return;
    }
    setItem(res.data);
    setEditing(false);
  };

  const onDelete = async () => {
    if (!item?.id) return;
    if (!window.confirm(`Delete item "${item.name}"?`)) return;
    setSaving(true);
    const res = await apiDelete<{ ok?: boolean }>(`/api/finance/items/${encodeURIComponent(item.id)}`);
    setSaving(false);
    if (!res.ok) {
      setError(res.error || "Delete failed.");
      return;
    }
    void navigate("/finance/items");
  };

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <Link
        to="/finance/items"
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All items
      </Link>

      {loading ? <p className="text-sm text-slate-500">Loading item…</p> : null}
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      {item && !loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Package className="h-8 w-8 text-indigo-600" aria-hidden />
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{item.name || "Item"}</h1>
                <p className="font-mono text-xs text-slate-500">{item.id}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {editing ? (
                <>
                  <button
                    type="button"
                    onClick={() => void onSave()}
                    disabled={saving || !name.trim()}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-white/20 dark:text-slate-200"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => void onDelete()}
                    disabled={saving}
                    className="rounded-md border border-rose-200 px-3 py-1.5 text-sm font-medium text-rose-700 dark:border-rose-900/50 dark:text-rose-300"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          {editing ? (
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="block text-sm sm:col-span-2">
                <span className="font-medium text-slate-700 dark:text-slate-300">Name</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="font-medium text-slate-700 dark:text-slate-300">Description</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">SKU</span>
                <input
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">Unit price</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                />
              </label>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                <span className="font-medium text-slate-700 dark:text-slate-300">Active in catalog</span>
              </label>
            </div>
          ) : (
            <dl className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { label: "SKU", value: item.sku?.trim() || "—" },
                {
                  label: "Price",
                  value:
                    typeof item.unitPrice === "number"
                      ? formatMoney(item.unitPrice, item.currency || "USD")
                      : "—",
                },
                { label: "Active", value: item.active === false ? "No" : "Yes" },
                { label: "Description", value: item.description?.trim() || "—" },
              ].map((row) => (
                <div key={row.label} className={row.label === "Description" ? "sm:col-span-2 lg:col-span-3" : undefined}>
                  <dt className="text-xs font-semibold uppercase text-slate-500">{row.label}</dt>
                  <dd className="mt-1 text-sm font-medium text-slate-900 dark:text-white">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ) : null}
    </div>
  );
}
